import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    AccessType,
    AggregateType,
    AuthService,
    DataSources,
    IMyScoreShiftQuery,
    ITrendingQuery,
    PermissionType,
    ShuntProductivityScoreExportQuery,
    ShuntProductivityScoreQuery,
    ShuntProductivityTrendingQuery,
    YardManagementClient,
} from '@services';
import {
    DxButtonComponent,
    DxDateRangeBoxComponent,
    DxSelectBoxComponent,
} from 'devextreme-angular';
import {
    ChartDataPoint,
    DoughnutChartComponent,
} from 'src/app/components/library/doughnut-chart/doughnut-chart.component';
import { PicturedItemSelectBoxComponent } from 'src/app/components/library/pictured-item-select-box/pictured-item-select-box.component';
import { TrendingComponent } from 'src/app/components/library/trending/trending.component';
import { CapitalCasePipe } from 'src/app/pipes/capital-case.pipe';
import { FileUtils } from 'src/app/shared/file-utils';
import EnumUtils from 'src/app/shared/enum-utils';

export function shuntProductivityPalette(): Record<string, string> {
    const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();

    return {
        ["Day"]: getColor('--bs-success'),
        ["Afternoons"]: getColor('--bs-info'),
        ["WeekendDays"]: getColor('--bs-warning'),
        ["WeekendNights"]: getColor('--bs-danger'),
    };
}

@Component({
    selector: 'app-shunt-productivity-score',
    imports: [
        CommonModule,
        DoughnutChartComponent,
        DxDateRangeBoxComponent,
        DxButtonComponent,
        DxSelectBoxComponent,
        CapitalCasePipe,
        DecimalPipe,
        PicturedItemSelectBoxComponent,
        TrendingComponent,
    ],
    templateUrl: './shunt-productivity-score.component.html',
})
export class ShuntProductivityScoreComponent implements OnInit {
    private yardManagementClient = inject(YardManagementClient);
    private destroyRef = inject(DestroyRef);
    private dataSources = inject(DataSources);
    private auth = inject(AuthService);

    userId: string | undefined;
    scoreData: ChartDataPoint[] = [];
    canManage: boolean = false;

    userDataSource = this.dataSources.shuntProductivityUserOptions();

    dateRangeFilterValue: [Date, Date] = [
        new Date(new Date().setHours(0, 0, 0, 0)),
        new Date(new Date().setHours(0, 0, 0, 0)),
    ];

    trendingFilterOptions = EnumUtils.getEnumOptions(AggregateType);
    trendingFilterValue: AggregateType = AggregateType.Day;

    palette: Record<string, string> = {};

    ngOnInit(): void {
        this.canManage = this.auth.checkPermission(PermissionType.ShuntProductivityScore, AccessType.Manage);

        if (!this.canManage) {
            this.userId = this.auth.userId ?? undefined;
        }

        this.palette = shuntProductivityPalette();
    }

    loadScoreData = (query: IMyScoreShiftQuery) => {
        return this.yardManagementClient.getShuntProductivityScore(new ShuntProductivityScoreQuery({
            from: query.from,
            to: query.to,
            userId: this.selectedUserId,
        }));
    }

    loadTrendingData = (query: ITrendingQuery) => {
        return this.yardManagementClient.getShuntProductivityTrending(new ShuntProductivityTrendingQuery({
            from: query.from,
            to: query.to,
            aggregateType: query.aggregateType,
            userId: this.selectedUserId,
        }));
    }

    private get selectedUserId(): string | undefined {
        return this.userId ?? undefined;
    }

    get fromDate(): Date {
        return this.dateRangeFilterValue?.[0];
    }

    get toDate(): Date {
        return this.dateRangeFilterValue?.[1];
    }

    onExport() {
        const query = new ShuntProductivityScoreExportQuery({
            from: this.fromDate,
            to: this.toDate,
            userId: this.selectedUserId,
        });

        this.yardManagementClient
            .exportShuntProductivity(query)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => FileUtils.downloadFile(res));
    }

    onMainChartLoaded(items: ChartDataPoint[]) {
        this.scoreData = items;
    }
}
